/**
 * Email Popup Component
 * Handles the display and submission of the email signup popup
 */
class EmailPopupComponent extends HTMLElement {
  connectedCallback() {
    // Get settings
    this.delay = parseInt(this.getAttribute('data-delay') || '3') * 1000;
    this.showOnExit = this.getAttribute('data-show-on-exit') === 'true';
    
    // Cookie names
    this.cookieName = 'email_popup_shown';
    this.submittedCookieName = 'email_popup_submitted';

    // Get elements
    this.overlay = this.querySelector('[ref="overlay"]');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    this.emailInput = this.querySelector('[ref="emailInput"]');
    this.submitButton = this.querySelector('[ref="submitButton"]');
    this.message = this.querySelector('[ref="message"]');
    this.form = this.querySelector('form');

    // Check if popup should be shown
    if (this.shouldShowPopup()) {
      this.initPopup();
    }
  }

  shouldShowPopup() {
    // Don't show if already submitted
    if (this.getCookie(this.submittedCookieName)) {
      return false;
    }

    // Don't show if shown recently (within 7 days)
    const lastShown = this.getCookie(this.cookieName);
    if (lastShown) {
      const daysSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      if (daysSinceShown < 7) {
        return false;
      }
    }

    return true;
  }

  initPopup() {
    // Show popup after delay
    setTimeout(() => {
      this.showPopup();
    }, this.delay);

    // Exit intent
    if (this.showOnExit) {
      this.setupExitIntent();
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.closePopup();
      });
    }

    // Click outside to close
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.closePopup();
        }
      });
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay && this.overlay.classList.contains('active')) {
        this.closePopup();
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        this.handleSubmit(e);
      });
    }
  }

  setupExitIntent() {
    let hasShown = false;
    
    document.addEventListener('mouseleave', (e) => {
      // Only trigger if mouse leaves from the top of the page
      if (e.clientY < 0 && !hasShown && this.overlay && !this.overlay.classList.contains('active')) {
        hasShown = true;
        this.showPopup();
      }
    });
  }

  showPopup() {
    if (this.overlay) {
      this.overlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';
    
    // Set cookie to track that popup was shown
    this.setCookie(this.cookieName, Date.now().toString(), 7);

    // Focus on email input
    setTimeout(() => {
      if (this.emailInput) {
        this.emailInput.focus();
      }
    }, 300);
  }

  closePopup() {
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }
    document.body.style.overflow = '';
  }

  handleSubmit(e) {
    if (!this.form) return;
    
    const email = this.emailInput ? this.emailInput.value : '';
    
    // Basic email validation
    if (!email || !this.isValidEmail(email)) {
      e.preventDefault();
      this.showMessage('error', 'Please enter a valid email address.');
      return;
    }

    // Don't prevent default - let form submit to iframe
    // Disable button during submission
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Subscribing...';
    }

    // Show success message after a short delay
    setTimeout(() => {
      this.showMessage('success', 'Thank you! Check your email for your 15% discount code.');
      
      // Set cookie to never show again after successful submission
      this.setCookie(this.submittedCookieName, 'true', 365);
      
      // Re-enable button
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = 'Get My Discount';
      }
      
      // Close popup after 3 seconds
      setTimeout(() => {
        this.closePopup();
      }, 3000);

      // Reset form
      if (this.form) {
        this.form.reset();
      }
    }, 500);
  }

  isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  showMessage(type, text) {
    if (this.message) {
      this.message.className = `email-popup__message ${type}`;
      this.message.textContent = text;
    }
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }

  disconnectedCallback() {
    document.body.style.overflow = '';
  }
}

if (!customElements.get('email-popup-component')) {
  customElements.define('email-popup-component', EmailPopupComponent);
}
